<template>
  <div
    :id="id + '_rep'"
    :class="{ gapped }"
    class="cat-repeater repeater-standard"
  >
    <component
      :is="headingComponent"
      v-if="title && title.trim().length > 0"
      :id="id + '_rep_lbl'"
      :class="reducedTitle ? 'label' : ''"
      class="repeater-title"
      data-label-type="repeater-title"
      tabindex="-1"
    >
      {{ title }}
    </component>
    <cat-hint v-if="hint" :id="id + '_hint'" :text="hint" />
    <cat-help
      v-if="inlineHelp && inlineHelp.text"
      :id="id + '_help'"
      :heading="inlineHelp.title"
      :text="inlineHelp.text"
      :aria-describedby="
        title && title.trim().length > 0 ? id + '_rep_lbl' : null
      "
    />
    <cat-error
      v-show="childCount == 0"
      :id="id"
      :level="instructionLevel"
      :message="instructionMessage"
    ></cat-error>
    <cat-errors
      :id="id"
      :disableDispatch="true"
      :container="true"
      :errors="errors"
    ></cat-errors>

    <div v-if="$slots.filters" class="repeater-filters">
      <slot name="filters" />
    </div>

    <div
      v-show="childCount > 0"
      :id="id + '-repeater-body'"
      class="repeater-body"
      role="list"
    >
      <slot></slot>
    </div>

    <div
      v-if="paging || !noAdd"
      class="repeater-bottom"
      :class="{ 'with-pagination': paging }"
    >
      <div v-if="paging" class="repeater-pagination">
        <cat-pagination
          :id="id"
          :paging="paging"
          :pagination="pagination"
          @ui-repeater-pagination-update="pageSelect"
        />
      </div>

      <div v-if="!noAdd" class="repeater-add-button">
        <slot name="add">
          <cat-btn
            v-if="!noAdd"
            :id="id + '_add'"
            secondary
            small
            :label="addButtonLabel"
            @ui-click="handleAddChild"
          ></cat-btn>
        </slot>
      </div>
    </div>

    <cat-repeater-callout
      v-if="callout"
      :id="id + '_selected'"
      :open="childDetail"
      :closeCaption="closeText"
      :dialog="modal"
      :size="modalSize"
      :calloutLabel="calloutLabel"
      :help="help"
      :panel-close="modalClose"
      @ui-repeater-done="handleChildDone"
    >
      <slot name="selected"></slot>
      <template #buttons>
        <slot v-if="childDetail" name="buttons"></slot>
      </template>
    </cat-repeater-callout>
  </div>
</template>

<script>
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatError from "@/components/catError/catError";
import CatErrors from "@/components/catErrors/catErrors";
import CatBtn from "@/components/catBtn/catBtn";
import CatRepeaterCallout from "./catRepeaterCallout";
import CatPagination from "./catPagination";
import CatHelp from "@/components/utilities/help.vue";
import CatHint from "@/components/utilities/hint.vue";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);

    return {
      resolveTxt,
      displayParams
    };
  },
  name: "CatRepeater",
  components: {
    CatPagination,
    CatError,
    CatErrors,
    CatRepeaterCallout,
    CatHelp,
    CatHint,
    CatBtn
  },
  props: {
    id: {
      type: [String, Number],
      description: "Repeater ID."
    },
    addButtonLabel: {
      type: String,
      default: "Add",
      description: "Text displayed on the button to add new record(s)."
    },
    selected: {
      type: Number,
      description: "Zero-based index of the currently selected record."
    },
    title: {
      type: String,
      description: "Heading displayed above the repeater."
    },
    titleLevel: {
      type: Number,
      default: 3,
      description: "Repeater heading level (relates to 'title')"
    },
    readOnly: {
      type: Boolean,
      description: "If 'true' repeater is read only."
    },
    instructionMessage: {
      type: String,
      description: "Text displayed when there is no repeater child or children."
    },
    instructionLevel: {
      type: Number,
      default: -1,
      description: "Repeater instruction level."
    },
    noAdd: {
      type: Boolean,
      default: false,
      description: "If true removes add repeater child button. For read only."
    },
    callout: {
      type: Boolean,
      default: true,
      description: "Displays modal window as slide out."
    },
    paging: {
      type: Boolean,
      default: false,
      description: "Adds page number."
    },
    pagination: {
      type: Object,
      description: "Adds pagination."
    },
    childCount: {
      type: Number,
      default: 0,
      description:
        "If 0 'instructionMessage' will be displayed. if > 0 repeater-child will be displayed."
    },
    errors: {
      type: Array,
      description: "If certain condition(s) are not met displays error(s)."
    },
    closeText: {
      type: String,
      description: "Modal close button text label"
    },
    calloutLabel: {
      type: String,
      description: "The heading text of the callout."
    },
    hint: {
      type: String,
      description: "Displays hint text below repeater header."
    },
    reducedTitle: {
      type: Boolean,
      default: false,
      description: "Applies 'label' class to repeater title."
    },
    help: {
      type: Object,
      description: "Help details for this repeater."
    },
    inlineHelp: {
      type: Object,
      description: "Inline help details for this repeater."
    },
    modal: {
      type: Boolean,
      default: false,
      description:
        "Displays the callout as a modal window rather then slideout - see modalSize prop for sizing "
    },
    modalSize: {
      type: String,
      description:
        "'xl' will result in class 'modal-size-xl' - predefined sizes are 's, m, l, xl'"
    },
    modalClose: {
      type: Boolean,
      description:
        "Controls if a 'close' button is shown for the selected record."
    },
    gapped: {
      type: Boolean,
      default: false,
      description: "Adds a gap between repeaters."
    }
  },
  data: function () {
    return {
      uploading: false,
      childDetail: false,
      selectedIndex: -1
    };
  },
  computed: {
    hasChildren: function () {
      // console.log("has children called", this._countChildren());
      // check data property as well as call method as computed values are cached and a data property needs to change for it to be re-evaluated
      return this.childDetail || this._countChildren() > 0;
    },
    modalClass: function () {
      return {
        modal: this.modal,
        ["modal-size-" + this.modalSize]: this.modalSize
      };
    },
    headingComponent: function () {
      return "h" + (this.titleLevel > 6 ? 6 : this.titleLevel);
    }
  },
  methods: {
    handleAddChild: function (event) {
      // console.log("ui-repeater-add firing");
      this.$emit("ui-repeater-add", {
        event: event,
        id: this.id ? this.id : null
      });
      this.toggleRepeaterChild();
    },
    handleChildDone: function (event) {
      // console.log(event);
      this.toggleRepeaterChild();
      this.$emit("ui-repeater-done", event);
    },
    toggleUploading: function () {
      this.uploading = !this.uploading;
    },
    toggleRepeaterChild: function () {
      this.childDetail = !this.childDetail;
      // jQuery('#' + this.id + '_panel').stop(true, true).toggleClass('move');
      // jQuery('.bg-layer').stop(true, true).fadeToggle();
    },
    pageSelect: function (event) {
      this.$emit("ui-repeater-pagination-update", event);
    }
  },
  watch: {
    selected: function (nw, ol) {
      //console.log("selected ", nw, ol);
      this.selectedIndex = nw;
      this.childDetail = nw != null && nw !== -1;
    }
  },
  mounted: function () {
    // console.log("Number of kids is :", this.childCount, this.selected);
    this.selectedIndex = !isNaN(parseInt(this.selected)) ? this.selected : -1;
    if (this.selectedIndex != -1) {
      // console.log("Selected is "+this.selectedIndex);
      this.childDetail = true;
    }
  }
});
</script>
